import { useNavigate } from "@tanstack/react-router";
import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import type { AssetTreeSearch } from "../../lib/assetTreeSearch";
import {
  isRetainedListStateEmpty,
  parseRetainedListState,
  pickAssetListSearch,
  pickRetainedListState,
  type RetainedListModule,
  type RetainedListScope,
  readRetainedListSnapshot,
  retainedListStorageKey,
  subscribeRetainedListState,
  writeRetainedListState,
} from "../../lib/retainedListState";
import { useWorkScope } from "../WorkScopeProvider";

/**
 * The storage scope of the signed-in user, customer, facility and role for a
 * module, or undefined while any of them is unknown (nothing is retained or
 * restored without one).
 */
export function useRetainedListScope(
  module: RetainedListModule,
): RetainedListScope | undefined {
  const { userId, selectedCustomer, selectedFacility } = useWorkScope();
  const customerId = selectedCustomer?.id;
  const facilityId = selectedFacility?.id;
  const role = selectedFacility?.role;
  return userId && customerId && facilityId && role
    ? { userId, customerId, facilityId, role, module }
    : undefined;
}

/**
 * Restores a module's retained list view on arrival and keeps remembering it.
 *
 * Restoring is decided once, when the list mounts: only when `restore` allows
 * it, a scope exists, the URL carries none of the module's list keys, and
 * something is stored. Explicit URL params always win. While restoring the
 * writer is paused (so the empty arrival URL cannot erase what is about to be
 * restored) and `restoring` is true so the route can show its pending state.
 */
export function useRetainedListSearch(
  module: RetainedListModule,
  urlSearch: Record<string, unknown>,
  { restore }: { restore: boolean },
): { restoring: boolean } {
  const scope = useRetainedListScope(module);
  const navigate = useNavigate();
  const [stored] = useState(() => {
    if (!restore || !scope) return undefined;
    if (
      !isRetainedListStateEmpty(
        module,
        pickRetainedListState(module, urlSearch),
      )
    )
      return undefined;
    const saved = parseRetainedListState(
      module,
      readRetainedListSnapshot(scope),
    );
    return isRetainedListStateEmpty(module, saved) ? undefined : saved;
  });
  const [restoring, setRestoring] = useState(stored !== undefined);
  const restored = useRef(false);

  useEffect(() => {
    if (!stored || restored.current) return;
    restored.current = true;
    void Promise.resolve(
      navigate({
        replace: true,
        search: ((previous: Record<string, unknown>) => ({
          ...previous,
          ...stored,
        })) as never,
      }),
    ).finally(() => setRestoring(false));
  }, [navigate, stored]);

  // An arrival that neither restores nor carries list keys (a direct link to
  // one record) has no view of its own to remember, so it must not erase the
  // stored one. Writing starts once the URL gains list keys; from then on an
  // empty URL (Clear Filters) is a real change and is stored.
  const awaitingListKeys = useRef(
    stored === undefined &&
      isRetainedListStateEmpty(
        module,
        pickRetainedListState(module, urlSearch),
      ),
  );
  useEffect(() => {
    if (restoring || !scope) return;
    if (awaitingListKeys.current) {
      const hasListKeys = !isRetainedListStateEmpty(
        module,
        pickRetainedListState(module, urlSearch),
      );
      if (!hasListKeys) return;
      awaitingListKeys.current = false;
    }
    writeRetainedListState(scope, urlSearch);
  }, [module, restoring, scope, urlSearch]);

  return { restoring };
}

/**
 * Retained state for a screen whose filters live in component state rather
 * than the URL: initialised from the session, written on every change.
 */
export function useRetainedLocalListState<T extends Record<string, unknown>>(
  module: RetainedListModule,
  defaults: T,
): [T, Dispatch<SetStateAction<T>>] {
  const scope = useRetainedListScope(module);
  const scopeKey = scope ? retainedListStorageKey(scope) : undefined;
  const load = (): T => ({
    ...defaults,
    ...(scope
      ? parseRetainedListState(module, readRetainedListSnapshot(scope))
      : {}),
  });
  const [state, setState] = useState<T>(load);
  // Reload when the scope becomes known or changes, so an earlier scope's
  // (or the pre-scope default) state is never written under the new key.
  const [loadedKey, setLoadedKey] = useState(scopeKey);
  if (loadedKey !== scopeKey) {
    setLoadedKey(scopeKey);
    setState(load());
  }
  useEffect(() => {
    if (scope && loadedKey === scopeKey) writeRetainedListState(scope, state);
  }, [scope, scopeKey, loadedKey, state]);
  return [state, setState];
}

/**
 * The Assets list view to reopen from a detail route or another module: the
 * list keys in the current URL when present, otherwise what the Assets list
 * last retained for this user, customer and facility in session storage.
 */
export function useRetainedAssetsSearch(
  currentSearch?: Record<string, unknown>,
): AssetTreeSearch {
  const scope = useRetainedListScope("assets");
  const stored = useSyncExternalStore(
    subscribeRetainedListState,
    () => (scope ? readRetainedListSnapshot(scope) : null),
    () => null,
  );
  const fromUrl = pickAssetListSearch(currentSearch);
  return isRetainedListStateEmpty("assets", fromUrl)
    ? (parseRetainedListState("assets", stored) as AssetTreeSearch)
    : fromUrl;
}

/**
 * Called by the Assets list route: remembers the list view for this user,
 * customer and facility so the Module breadcrumb and navigation can reopen it.
 */
export function useRetainAssetsSearch(search: AssetTreeSearch): void {
  const scope = useRetainedListScope("assets");
  useEffect(() => {
    if (scope) writeRetainedListState(scope, search);
  }, [scope, search]);
}
